/**
 * Perth Property Pulse — individual Reading Room note.
 * Route: /perth-property-pulse/articles/:slug
 *
 * Editorial article layout in the .ppp-* design language. SEO: runtime meta +
 * Article + BreadcrumbList JSON-LD. Unpublished or unknown slugs redirect back
 * to the Reading Room. Reuses the main page stylesheet for shared tokens.
 */
import React, { useEffect } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Clock, ExternalLink, ShieldCheck } from 'lucide-react';
import { ARTICLES, getArticle } from '../data/perthArticles';
import '../styles/PerthPropertyPulse.css';
import '../styles/PerthPropertyArticle.css';

const SITE_URL = 'https://allsecured.com.au';

const setTag = (selector, attrs) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(selector.split('[')[0]);
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
};

const useArticleSEO = (article) => {
  useEffect(() => {
    if (!article) return undefined;
    const prevTitle = document.title;
    const url = `${SITE_URL}/perth-property-pulse/articles/${article.slug}/`;
    const title = `${article.title} | Perth Property Pulse`;
    const description = article.dek || article.excerpt;
    document.title = title;
    const created = [];
    const ensure = (sel, attrs) => {
      if (!document.head.querySelector(sel)) created.push(sel);
      setTag(sel, attrs);
    };
    ensure('meta[name="description"]', { name: 'description', content: description });
    ensure('link[rel="canonical"]', { rel: 'canonical', href: url });
    ensure('meta[property="og:title"]', { property: 'og:title', content: title });
    ensure('meta[property="og:description"]', { property: 'og:description', content: description });
    ensure('meta[property="og:type"]', { property: 'og:type', content: 'article' });
    ensure('meta[property="og:url"]', { property: 'og:url', content: url });
    ensure('meta[property="og:image"]', { property: 'og:image', content: article.image });
    ensure('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
    ensure('meta[name="twitter:title"]', { name: 'twitter:title', content: title });
    ensure('meta[name="twitter:description"]', { name: 'twitter:description', content: description });

    const ldId = 'ppp-article-jsonld';
    let ld = document.getElementById(ldId);
    if (!ld) {
      ld = document.createElement('script');
      ld.id = ldId;
      ld.type = 'application/ld+json';
      document.head.appendChild(ld);
    }
    ld.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Article',
          headline: article.title,
          description,
          image: article.image,
          articleSection: article.category,
          url,
          author: { '@type': 'Organization', name: 'Perth Property Pulse', url: `${SITE_URL}/perth-property-pulse/` },
          publisher: { '@type': 'Organization', name: 'All Secured', url: `${SITE_URL}/` },
          isPartOf: { '@type': 'WebSite', name: 'All Secured', url: `${SITE_URL}/` },
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
            { '@type': 'ListItem', position: 2, name: 'Perth Property Pulse', item: `${SITE_URL}/perth-property-pulse/` },
            { '@type': 'ListItem', position: 3, name: 'The Reading Room', item: `${SITE_URL}/perth-property-pulse/` },
            { '@type': 'ListItem', position: 4, name: article.title, item: url },
          ],
        },
      ],
    });

    window.scrollTo(0, 0);
    return () => {
      document.title = prevTitle;
      document.getElementById(ldId)?.remove();
      created.forEach((sel) => document.head.querySelector(sel)?.remove());
    };
  }, [article]);
};

const Block = ({ block }) => {
  if (block.type === 'h2') return <h2 className="ppa-h2 ppp-serif">{block.text}</h2>;
  if (block.type === 'p') return <p className="ppa-p">{block.text}</p>;
  if (block.type === 'list') {
    return (
      <ul className="ppa-list">
        {block.items.map((item) => (
          <li key={item}><span className="ppp-mini-dot" /><span>{item}</span></li>
        ))}
      </ul>
    );
  }
  if (block.type === 'callout') {
    return (
      <aside className="ppa-callout">
        {block.title && <div className="ppa-callout-title">{block.title}</div>}
        <p>{block.text}</p>
      </aside>
    );
  }
  return null;
};

const PerthPropertyArticle = () => {
  const { slug } = useParams();
  const article = getArticle(slug);
  useArticleSEO(article);

  if (!article || !article.published) {
    return <Navigate to="/perth-property-pulse" replace />;
  }

  const more = ARTICLES.filter((a) => a.slug !== article.slug).slice(0, 3);

  return (
    <div className="ppp-page ppa" data-testid="ppp-article-page">
      {/* HEADER */}
      <header className="ppp-header">
        <div className="ppp-header-inner">
          <Link to="/perth-property-pulse" className="ppp-brand" data-testid="ppa-brand">
            <span className="ppp-brand-pulse" />
            <div className="ppp-brand-text">Perth Property Pulse <span>· by All Secured</span></div>
          </Link>
          <nav className="ppp-header-nav">
            <Link className="ppp-header-cta" to="/perth-property-pulse" data-testid="ppa-back-cta">
              The Reading Room <ArrowRight size={13} />
            </Link>
          </nav>
        </div>
      </header>

      {/* ARTICLE HERO */}
      <article>
        <div className="ppa-hero">
          <div className="ppa-hero-bg" style={{ backgroundImage: `url(${article.image})` }} role="img" aria-label={article.title} />
          <div className="ppa-hero-veil" />
          <div className="ppa-hero-inner">
            <Link to="/perth-property-pulse" className="ppa-crumb" data-testid="ppa-crumb">
              <ArrowLeft size={14} /> The Reading Room
            </Link>
            <span className="ppa-cat">{article.category}</span>
            <h1 className="ppa-title ppp-serif">{article.title}</h1>
            <p className="ppa-dek">{article.dek}</p>
            <div className="ppa-meta">
              <span><Clock size={13} /> {article.readTime}</span>
              {article.updated && <span className="ppa-meta-dot">Updated {article.updated}</span>}
              <span className="ppa-meta-dot">Sourced &amp; reviewed</span>
            </div>
          </div>
        </div>

        {/* BODY */}
        <div className="ppa-body">
          {article.body.map((block, i) => <Block block={block} key={i} />)}

          {/* Sources */}
          {article.sources?.length > 0 && (
            <section className="ppa-sources" data-testid="ppa-sources">
              <div className="ppp-form-eyebrow">Sources</div>
              <h2 className="ppa-sources-title ppp-serif">Where this came from</h2>
              <ul className="ppa-sources-list">
                {article.sources.map((s) => (
                  <li key={s.name}>
                    <a href={s.url} target="_blank" rel="noopener noreferrer" className="ppa-source-name">
                      {s.name} <ExternalLink size={13} />
                    </a>
                    <p className="ppa-source-detail">{s.detail}</p>
                  </li>
                ))}
              </ul>
              <p className="ppa-source-note">
                <ShieldCheck size={15} /> Figures are drawn from the named sources and reference periods above.
                Property data is revised over time and is general information only — not financial, legal or property advice.
              </p>
            </section>
          )}
        </div>
      </article>

      {/* MORE FROM THE READING ROOM */}
      <section className="ppp-section ppp-section-sand">
        <div className="ppp-container">
          <div className="ppp-section-head">
            <span className="ppp-eyebrow">The Reading Room</span>
            <h2 className="ppp-h2">More to read</h2>
          </div>
          <div className="ppp-grid-3">
            {more.map((a) => {
              const Card = (
                <>
                  <div className="ppp-article-img" style={{ backgroundImage: `url(${a.image})` }} role="img" aria-label={a.title}>
                    <span className="ppp-article-cat">{a.category}</span>
                  </div>
                  <div className="ppp-article-body">
                    <h3 className="ppp-article-title">{a.title}</h3>
                    <p className="ppp-article-excerpt">{a.excerpt}</p>
                    <div className="ppp-article-foot">
                      <span className="ppp-read-time"><Clock size={11} style={{ verticalAlign: '-1px', marginRight: 4 }} />{a.readTime}</span>
                      <span className="ppp-article-btn" aria-hidden="true">{a.published ? 'Read the Note' : 'Coming soon'} <ArrowRight size={13} /></span>
                    </div>
                  </div>
                </>
              );
              return a.published ? (
                <Link className="ppp-article" to={`/perth-property-pulse/articles/${a.slug}`} key={a.slug} style={{ textDecoration: 'none' }}>{Card}</Link>
              ) : (
                <article className="ppp-article ppa-article-soon" key={a.slug}>{Card}</article>
              );
            })}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="ppp-footer">
        <div className="ppp-footer-inner">
          <Link to="/perth-property-pulse" className="ppp-footer-brand">
            <span className="ppp-brand-pulse" /> Perth Property Pulse
          </Link>
          <nav className="ppp-footer-links">
            <Link to="/perth-property-pulse">Back to the Pulse</Link>
            <Link to="/">All Secured Home</Link>
            <Link to="/privacy-policy">Privacy</Link>
          </nav>
        </div>
        <p className="ppp-footer-fine">
          Perth Property Pulse is created by All Secured. Everything here is general information only and is not
          financial, legal, planning or property advice. Always check official sources and qualified professionals
          before making property decisions.
        </p>
      </footer>
    </div>
  );
};

export default PerthPropertyArticle;
